import { DeleteOutlined, SettingOutlined, TeamOutlined } from "@ant-design/icons";
import { Button, Card, Col, Modal, Popconfirm, Row, Typography, Skeleton, Result } from "antd";
import axios from "axios";
import { message } from "@bindeleddet/core/utils";
import { Event, IsOpenType, NoShow, RegistrationWithStudent } from "@bindeleddet/core/types";
import { useCallback, useEffect, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import AdminSingleEventHeader from "./AdminEventHeader";
import EventStats from "./Statistics/EventStats";
import ClassYearDistribution from "./Statistics/ClassyearDistribution";
import StudyProgramDistribution from "./Statistics/StudyProgramDistribution";
import QRCodeEvent from "./QRcode";
import DeregisteredStudents from "./RegistrationLists/DeregisteredStudents";
import RegisteredStudents from "./RegistrationLists/RegisteredStudents";
import Waitlist from "./RegistrationLists/Waitlist";
import { NoShows } from "./NoShows";
import SendEmailModal from "./SendEmailModal";

const { Text, Paragraph } = Typography;

export interface RegisteredStudentData {
  key: number;
  id: number;
  name: string;
  username: string;
  study_programme_id: number;
  study_programme_short_name: string;
  study_programme_name: string;
  year_level: number;
  mobile_number: string;
  email: string;
  allergies: string;
  time_attended?: string;
  created_at: string;
  number: number;
}

interface StudentsData {
  registered: RegisteredStudentData[];
  waitlist: RegisteredStudentData[];
  deregistered: RegisteredStudentData[];
}

const AdminSingleEventPage = () => {
  const [event, setEvent] = useState<Event>();
  const { id } = useParams<{ id: string }>();
  const [loadingStudents, setLoadingStudents] = useState<boolean>(true);
  const [noShows, setNoShows] = useState<NoShow[]>([]);
  const [fetchingEvent, setFetchingEvent] = useState<boolean>(true);

  const [studentsData, setStudentsData] = useState<StudentsData>({
    registered: [],
    waitlist: [],
    deregistered: [],
  });

  const browserHistory = useHistory();

  useEffect(() => {
    axios
      .get<Event>(`/events/admin-events/${id}/`)
      .then(response => {
        setEvent(response.data);
      })
      .finally(() => {
        setFetchingEvent(false);
      });
  }, [id]);

  useEffect(() => {
    axios.get<NoShow[]>(`/events/${id}/no-shows/`).then(response => {
      setNoShows(response.data);
    });
  }, [event?.no_shows_sent, event, id]);

  const fetchRegistrationsData = useCallback(async () => {
    if (!event) return;
    setLoadingStudents(true);
    try {
      const [registrationsRes, waitlistRes, deregistrationsRes] = await Promise.all([
        axios.get<RegistrationWithStudent[]>(`/events/${event.id}/registrations-with-students/`),
        axios.get<RegistrationWithStudent[]>(`/events/${event.id}/waitlist-with-students/`),
        axios.get<RegistrationWithStudent[]>(`/events/${event.id}/deregistrations-with-students/`),
      ]);

      const mapToStudentData = (
        items: RegistrationWithStudent[],
        sortAsc: boolean,
        reverseNumbers: boolean
      ): RegisteredStudentData[] => {
        const sorted = [...items].sort((a, b) => {
          const timeA = new Date(a.created_at).getTime();
          const timeB = new Date(b.created_at).getTime();
          return sortAsc ? timeA - timeB : timeB - timeA;
        });
        return sorted.map((reg, index) => ({
          key: reg.id,
          id: reg.student,
          name: `${reg.first_name} ${reg.last_name}`,
          username: reg.username,
          study_programme_id: reg.study_programme_id,
          study_programme_short_name: reg.study_programme_shortname,
          study_programme_name: reg.study_programme_name,
          year_level: reg.year_level,
          mobile_number: String(reg.mobile_number || ""),
          email: reg.email || "",
          allergies: reg.allergies || "",
          time_attended: reg.time_attended
            ? new Date(reg.time_attended).toLocaleString("nb-NO", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
              })
            : undefined,
          created_at: reg.created_at,
          number: reverseNumbers ? items.length - index : index + 1,
        }));
      };

      setStudentsData({
        registered: mapToStudentData(registrationsRes.data, true, false),
        waitlist: mapToStudentData(waitlistRes.data, false, true),
        deregistered: mapToStudentData(deregistrationsRes.data, true, false),
      });

      setLoadingStudents(false);
    } catch {
      setLoadingStudents(false);
    }
  }, [event]);

  const handleCloseEvent = () => {
    axios
      .put<Event>(`/events/admin-events/${id}/`, {
        ...event,
        location: event?.location?.id ?? event?.location,
        company: event?.company?.id,
        manually_closed: true,
      })
      .then(response => {
        message("success", "Arrangementet er stengt for påmelding og avmelding");
        setEvent(response.data);
      })
      .catch(() => {
        message("error", "Kunne ikke stenge arrangementet");
      });
  };

  const handleOpenEvent = () => {
    axios
      .put<Event>(`/events/admin-events/${id}/`, {
        ...event,
        location: event?.location?.id ?? event?.location,
        company: event?.company?.id,
        manually_closed: false,
      })
      .then(response => {
        message("success", "Arrangementet er åpnet igjen.");
        setEvent(response.data);
      })
      .catch(() => {
        message("error", "Kunne ikke åpne arrangementet");
      });
  };

  const handleInnslipp = () => {
    if (!event || event.is_open !== IsOpenType.CLOSED) {
      message("warning", "Steng arrangementet først for å starte innslipp.");
      return;
    }
    browserHistory.push(`/admin/tools/take-attendance-for-event/${id}`);
  };

  const handleDeleteEvent = () => {
    axios
      .delete<Event>(`/events/admin-events/${id}/`)
      .then(() => {
        message("success", "Arrangementet er slettet.");
        browserHistory.push("/admin/events/");
      })
      .catch(() => {
        message("error", "Kunne ikke slette arrangementet.");
      });
  };

  const handleSendNoShow = () => {
    if (!event || event.is_open !== IsOpenType.CLOSED) {
      message("warning", "Steng arrangementet først for å sende No Show.");
      return;
    }

    Modal.confirm({
      title: "Send No Show",
      content: (
        <>
          {event.is_open !== IsOpenType.CLOSED ? (
            <Text>Steng arrangementet først for å sende No Show.</Text>
          ) : (
            <>
              <Paragraph>
                Er du sikker på at du vil sende No Show til alle påmeldte studenter som ikke har registrert oppmøte?
              </Paragraph>
              <Paragraph>
                Følgende studenter vil få en prikk: <br />
                <ul>
                  {studentsData.registered
                    .filter(student => !student.time_attended)
                    .map(student => (
                      <li key={student.username}>{student.name}</li>
                    ))}
                  {studentsData.registered.filter(student => !student.time_attended).length === 0 && (
                    <li>Ingen studenter</li>
                  )}
                </ul>
                <Text italic>
                  Hvis noen i listen har registrert oppmøte etter du åpnet denne siden vil de ikke få prikk.
                </Text>
              </Paragraph>
            </>
          )}
        </>
      ),
      okText: "Send No Show",
      okType: "danger",
      cancelText: "Avbryt",
      onOk: () => {
        axios
          .post(`/events/${id}/send-no-show/`)
          .then(() => {
            message("success", "No Show er sendt");
            setEvent({ ...event, no_shows_sent: true });
            fetchRegistrationsData();
          })
          .catch(() => {
            message("error", "Feil under sending av No Show");
          });
      },
    });
  };

  useEffect(() => {
    fetchRegistrationsData();
  }, [fetchRegistrationsData]);

  if (fetchingEvent)
    return (
      <>
        <Row>
          <Skeleton.Button block active style={{ width: "100%", height: "150px" }} />
        </Row>
        <Row>
          <Skeleton.Button block active style={{ height: "75px", margin: "16px 0" }} />
        </Row>
        <Row gutter={[16, 16]}>
          <Col lg={4} md={8} sm={12} xs={24}>
            <Skeleton.Button block active style={{ height: "150px" }} />
          </Col>
          <Col lg={4} md={8} sm={12} xs={24}>
            <Skeleton.Button block active style={{ height: "150px" }} />
          </Col>
          <Col lg={4} md={8} sm={12} xs={24}>
            <Skeleton.Button block active style={{ height: "150px" }} />
          </Col>
          <Col lg={4} md={8} sm={12} xs={24}>
            <Skeleton.Button block active style={{ height: "150px" }} />
          </Col>
          <Col lg={4} md={8} sm={12} xs={24}>
            <Skeleton.Button block active style={{ height: "150px" }} />
          </Col>
          <Col lg={4} md={8} sm={12} xs={24}>
            <Skeleton.Button block active style={{ height: "150px" }} />
          </Col>
        </Row>
        <Row style={{ marginTop: "16px" }}>
          <Skeleton.Button block active style={{ height: "300px" }} />
        </Row>
        <Row style={{ marginTop: "16px" }}>
          <Skeleton.Button block active style={{ height: "300px" }} />
        </Row>
        <Row style={{ marginTop: "16px" }}>
          <Skeleton.Button block active style={{ height: "300px" }} />
        </Row>
      </>
    );

  if (!event)
    return <Result title="Fant ikke arrangementet" subTitle="Dobbeltsjekk URL eller prøv på nytt" status="404" />;

  return (
    <>
      <AdminSingleEventHeader event={event} />
      <Card style={{ width: "100%", marginTop: "16px" }}>
        <Row gutter={[16, 16]}>
          <Col lg={8} sm={24} style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            {event.manually_closed ? (
              // Event is manually closed - show reopen, innslipp, and send no show buttons
              !event.no_shows_sent ? (
                <>
                  <Popconfirm
                    key="open-event"
                    title="Sikker på at du vil åpne arrangementet igjen?"
                    okText="Åpne"
                    cancelText="Avbryt"
                    onConfirm={handleOpenEvent}
                  >
                    <Button type="primary">Åpne</Button>
                  </Popconfirm>
                  <Button type="primary" onClick={handleInnslipp}>
                    Innslipp
                  </Button>
                  <Button danger onClick={handleSendNoShow}>
                    Send No Show
                  </Button>
                </>
              ) : (
                <Button disabled>Fullført - Arrangementet er stengt og No Show er sendt</Button>
              )
            ) : (
              // Event is not manually closed - show close button and maybe innslipp/send no show
              <>
                <Popconfirm
                  key="close-event"
                  title="Sikker på at du vil stenge for påmelding og avmelding?"
                  okText="Steng"
                  cancelText="Avbryt"
                  okType="danger"
                  onConfirm={handleCloseEvent}
                >
                  <Button type="primary" danger>
                    Steng
                  </Button>
                </Popconfirm>
                {new Date(event.start) < new Date() && !event.no_shows_sent && (
                  <>
                    <Button type="primary" onClick={handleInnslipp}>
                      Innslipp
                    </Button>
                    <Button danger onClick={handleSendNoShow}>
                      Send No Show
                    </Button>
                  </>
                )}
              </>
            )}
          </Col>
          <Col lg={16} sm={24} style={{ textAlign: "right" }}>
            <SendEmailModal eventId={event.id} registeredStudents={studentsData.registered} />
            <Link to={`/admin/events/${id}/edit`}>
              <Button type="primary" title="Rediger arrangementet">
                <SettingOutlined />
              </Button>
            </Link>
            <Link to={`/admin/events/${id}/seats`}>
              <Button type="primary" title="Rediger plassfordeling" style={{ margin: "0 5px" }}>
                <TeamOutlined />
              </Button>
            </Link>
            <QRCodeEvent eventId={event.id} />
            <Popconfirm
              key="delete-event"
              title="Sikker på at du vil slette arrangementet?"
              okText="Slett"
              cancelText="Avbryt"
              okType="danger"
              onConfirm={handleDeleteEvent}
            >
              <Button type="primary" title="Slett arrangementet" danger>
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          </Col>
        </Row>
      </Card>
      {(event.no_shows_sent || noShows.length > 0) && (
        <Row>
          <NoShows noShows={noShows} />
        </Row>
      )}
      <Row style={{ marginBottom: "16px" }}>
        <EventStats
          seats={event.seats}
          waitingListSize={event?.use_waiting_list ? event?.waiting_list_size : 0}
          waitlist={studentsData.waitlist}
          registeredStudents={studentsData.registered}
          noShows={noShows.filter(n => n.no_show_type === 0).length}
        />
      </Row>
      {studentsData.registered.length > 0 && (
        <Row
          gutter={[16, 16]}
          style={{ width: "calc(100% + 16px)", display: "flex", alignItems: "stretch", marginBottom: "16px" }}
        >
          <Col md={12} sm={24} xs={24}>
            <ClassYearDistribution registeredStudents={studentsData.registered} />
          </Col>
          <Col md={12} sm={24} xs={24}>
            <StudyProgramDistribution registeredStudents={studentsData.registered} />
          </Col>
        </Row>
      )}
      <Row>
        <Waitlist
          loading={loadingStudents}
          registeredStudentsData={studentsData.waitlist}
          onStudentRegistered={fetchRegistrationsData}
          eventId={event.id}
        />
      </Row>
      <Row>
        <RegisteredStudents
          loading={loadingStudents}
          eventId={event.id}
          registeredStudentsData={studentsData.registered}
          onStudentDeregistered={(studentId: number) => {
            setStudentsData(prevData => ({
              ...prevData,
              registered: prevData.registered.filter(s => s.id !== studentId),
            }));
            fetchRegistrationsData(); // Fetch to see if someone is registered from waitlist etc.
          }}
          onStudentRegistered={fetchRegistrationsData}
        />
      </Row>
      <Row>
        <DeregisteredStudents loading={loadingStudents} registeredStudentsData={studentsData.deregistered} />
      </Row>
    </>
  );
};

export default AdminSingleEventPage;
